/**
 * BroadcastRefreshListener — escucha invalidaciones globales pusheadas
 * por el superadmin desde Ajustes → Mantenimiento.  Se monta en el
 * árbol principal de la app (App.js), así corre en TODAS las vistas —
 * PWA admin, PWA paciente, kiosco, TV, incluso páginas públicas.
 *
 * Flujo:
 *   1. Al montar, hacemos un GET inicial a /api/broadcasts/latest y
 *      guardamos el `refresh_all_ts` actual como baseline en
 *      localStorage.  Sin baseline no destacamos nada — igual que en
 *      el WaitingRoomBadge, evitamos falsos positivos al primer render.
 *   2. Poll cada 60 s (pausado con document.hidden).
 *   3. Si el `refresh_all_ts` recibido es POSTERIOR al baseline:
 *        - Actualizamos el baseline en localStorage.
 *        - Mostramos toast "Nueva versión disponible — recargando…".
 *        - Desregistramos service workers y borramos todos los caches.
 *        - `window.location.reload()` a los 2 s.
 *
 *   Guardar el baseline ANTES de recargar es crítico — si no, el
 *   próximo render tras el reload volvería a detectar "cambio" y
 *   entraría en bucle de recarga.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;
const STORAGE_KEY = "broadcast_ack_ts";

async function nukeCachesAndReload(reason) {
  // OJO: NO desregistramos los service workers — hacerlo destruye la
  // suscripción Web Push del navegador (los avisos del admin quedaban
  // "desactivados" tras cada deploy). Basta con vaciar caches y pedir
  // al SW que compruebe si hay versión nueva.
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => false)));
    }
  } catch { /* silent */ }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* silent */ }

  try {
    toast(reason ? `Actualizando: ${reason}` : "Actualizando la aplicación…", {
      duration: 1800,
      position: "top-center",
      className: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    });
  } catch { /* silent */ }

  setTimeout(() => { window.location.reload(); }, 2000);
}

export default function BroadcastRefreshListener() {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(`${API}/api/broadcasts/latest`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const ts = data?.refresh_all_ts;
        if (!ts) return;
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          // Baseline inicial — no recargamos, solo guardamos.
          localStorage.setItem(STORAGE_KEY, ts);
          return;
        }
        if (ts > stored) {
          // Marcamos ACK ANTES de recargar para evitar bucles.
          localStorage.setItem(STORAGE_KEY, ts);
          if (!cancelled) nukeCachesAndReload(data?.reason);
        }
      } catch { /* silent — reintenta en el siguiente tick */ }
    };

    check();
    const timer = setInterval(() => { if (!document.hidden) check(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return null;
}
